import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Label } from "../../@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { api_endpoint } from "@/constants/constant";
import { ClipLoader } from "react-spinners";

const TYPE_OPTIONS = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
];

function ensureIsoDateTime(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  // Already an ISO-like datetime.
  if (raw.includes("T")) return raw;

  // Date-only.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T00:00:00`;

  // Last resort: attempt parsing.
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toISOString();
}

function normalizeType(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  const allowed = new Set(TYPE_OPTIONS.map((o) => o.value));
  return allowed.has(raw) ? raw : "full-time";
}

export default function BatchUpdateCohortsType({
  open,
  onOpenChange,
  selectedCohorts,
  onUpdated,
    refetchCohorts,
}) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("full-time");
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    if (!open) return;
    setType("full-time");
    setErrorDetails(null);
  }, [open]);

  const normalizedCohorts = useMemo(() => {
    const rows = Array.isArray(selectedCohorts) ? selectedCohorts : [];
    return rows
      .map((row) => {
        const raw = row?.raw ?? row;
        return {
          cohort_id: raw?.cohort_id ?? raw?.cohortId ?? raw?.id ?? row?.id,
          name: raw?.name ?? row?.name,
          start_date: ensureIsoDateTime(raw?.start_date ?? raw?.start ?? row?.start_date ?? row?.start),
          end_date: ensureIsoDateTime(raw?.end_date ?? raw?.end ?? row?.end_date ?? row?.end),
        };
      })
      .filter((c) => c.cohort_id != null)
      .map((c) => ({
        cohort_id: Number(c.cohort_id),
        name: c?.name != null ? String(c.name) : "",
        start_date: c.start_date,
        end_date: c.end_date,
      }))
      .filter((c) => Number.isFinite(c.cohort_id));
  }, [selectedCohorts]);

  const typeLabel = useMemo(() => {
    const found = TYPE_OPTIONS.find((o) => o.value === type);
    return found?.label ?? type;
  }, [type]);

  const canSubmit = normalizedCohorts.length > 0 && Boolean(String(type).trim());

  const handleSave = async () => {
    if (!canSubmit) {
      toast.error("Select at least one cohort.");
      return;
    }

    setLoading(true);
    setErrorDetails(null);

    const missingRequired = normalizedCohorts.filter(
      (c) => !String(c?.name || "").trim() || !c?.start_date || !c?.end_date
    );

    if (missingRequired.length > 0) {
      const first = missingRequired[0];
      const msg = `Some selected cohorts are missing required fields (name/start_date/end_date). Example cohort_id: ${first?.cohort_id}`;
      setErrorDetails({
        status: 422,
        message: msg,
        responseData: {
          detail: missingRequired.map((c) => ({
            cohort_id: c.cohort_id,
            missing: {
              name: !String(c?.name || "").trim(),
              start_date: !c?.start_date,
              end_date: !c?.end_date,
            },
          })),
        },
        payload: null,
      });
      toast.error("Cannot submit: missing required cohort fields.");
      setLoading(false);
      return;
    }

    const payload = {
      cohorts: normalizedCohorts.map((c) => ({
        cohort_id: c.cohort_id,
        name: c.name,
        type: normalizeType(type),
        start_date: c.start_date,
        end_date: c.end_date,
      })),
    };

    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const res = await axios.put(`${api_endpoint}/cohort/batch-update`, payload, reqConfig);

      const data = res?.data;
      const totalUpdated =
        data?.total_updated != null ? Number(data.total_updated) : null;
      const totalErrors =
        data?.total_errors != null ? Number(data.total_errors) : null;

      const updatedCount = Number.isFinite(totalUpdated)
        ? totalUpdated
        : normalizedCohorts.length;

      if (Number.isFinite(totalErrors) && totalErrors > 0) {
        toast.success(
          `Updated ${updatedCount} cohort${updatedCount === 1 ? "" : "s"}. ${totalErrors} failed.`
        );
      } else {
        toast.success(
          `Updated ${updatedCount} cohort${updatedCount === 1 ? "" : "s"} to "${typeLabel}".`
        );
      }

      onOpenChange(false);
      onUpdated?.(data);
        // Defer so parent refetch isn't affected by dialog state batching.
        if (refetchCohorts) setTimeout(() => refetchCohorts(), 0);
    } catch (error) {
      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const responseHeaders = error?.response?.headers;
      const message =
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        "Request failed";

      console.error("Error batch-updating cohorts:", {
        status,
        message,
        responseData,
        responseHeaders,
        payload,
      });

      setErrorDetails({
        status,
        message,
        responseData,
        payload,
      });

      toast.error(status ? `Failed to update cohorts (${status}).` : "Failed to update cohorts.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyErrorDetails = async () => {
    try {
      const text = JSON.stringify(
        {
          request: {
            url: `${api_endpoint}/cohort/batch-update`,
            method: "PUT",
            payload: errorDetails?.payload,
          },
          response: {
            status: errorDetails?.status,
            message: errorDetails?.message,
            data: errorDetails?.responseData,
          },
        },
        null,
        2
      );
      await navigator.clipboard.writeText(text);
      toast.success("Copied debug details.");
    } catch (_err) {
      toast.error("Could not copy debug details.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Bulk Edit Cohort Type
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-(--neutral-300) bg-(--surface-base) p-4">
            <div className="font-heading text-(--neutral-900)">
              {normalizedCohorts.length} selected
            </div>
            <div className="mt-1 font-body text-sm text-(--neutral-600)">
              This will update only the <b>Type</b> field.
            </div>
          </div>

          <div className="rounded-md border border-(--neutral-300) bg-(--surface-panel) p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="font-body text-(--neutral-800)">New Type</Label>
                <div className="mt-1 font-body text-xs text-(--neutral-600)">
                  Choose the cohort type to apply to all.
                </div>
              </div>
              <div className="min-w-40">
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="w-full bg-(--surface-base)">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {errorDetails ? (
            <div className="rounded-md border border-(--color-destructive) bg-(--surface-base) p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-(--color-destructive)">
                    Update failed{errorDetails?.status ? ` (${errorDetails.status})` : ""}
                  </div>
                  <div className="mt-1 font-body text-sm text-(--neutral-700)">
                    {String(errorDetails?.message || "The server rejected the request.")}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="border-(--neutral-300)"
                  onClick={handleCopyErrorDetails}
                >
                  Copy debug
                </Button>
              </div>

              <div className="mt-3 grid gap-3">
                <div>
                  <div className="font-body text-xs font-semibold text-(--neutral-700)">
                    Request payload
                  </div>
                  <pre className="mt-1 max-h-40 overflow-auto rounded bg-(--neutral-100) p-2 text-xs text-(--neutral-900)">
                    {JSON.stringify(errorDetails?.payload, null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="font-body text-xs font-semibold text-(--neutral-700)">
                    Response body
                  </div>
                  <pre className="mt-1 max-h-40 overflow-auto rounded bg-(--neutral-100) p-2 text-xs text-(--neutral-900)">
                    {JSON.stringify(errorDetails?.responseData, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex gap-2 justify-between">
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button variant="" onClick={handleSave} disabled={loading || !canSubmit}>
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Update"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
